/**
 * API Client
 * Handles authenticated requests to the API
 */

import { isLocalDevelopmentHost } from "@games-monorepo/shared-utils/hosts";
import {
	generateMarchYaknessSessionSignature,
	MARCH_YAKNESS_SESSION_SIGNATURE_HEADER,
} from "@games-monorepo/shared-utils/marchYakness";
import { captureError, captureMessage } from "@games-monorepo/shared-utils/observability";
import { getApiBaseUrl } from "@games-monorepo/shared-utils/apiBaseCore";
import { getPublicAuthToken } from "@games-monorepo/shared-utils/publicEnv";
import { STORAGE_KEYS } from "@games-monorepo/shared-utils/storageKeys";
import {
	hasNativeAppContextBridge,
	hydrateAppContextFromNativeBridge,
} from "./appContext";
import { ApiError } from "./types";
import type { RequestConfig } from "./types";

export const isNetworkRequestError = (error: unknown): error is TypeError =>
	error instanceof TypeError &&
	/^(?:failed to fetch|load failed|networkerror\b)/i.test(error.message);

class ApiClient {
	private baseUrl: string | null = null;
	private tokenReadyPromise: Promise<string> | null = null;
	private requestCounter = 0;
	private static readonly AUTH_TOKEN_WAIT_TIMEOUT_MS = 12000;
	private static readonly REDACTED_AUTH_HEADER = "Bearer [redacted]";
	private static readonly REDACTED_SENSITIVE_HEADER = "[redacted]";
	private static readonly REDACTED_SENSITIVE_PAYLOAD = "[redacted sensitive payload]";
	private static readonly BACKEND_REASON_MAX_LENGTH = 240;

	constructor() {
		if (hasNativeAppContextBridge()) {
			void hydrateAppContextFromNativeBridge({
				reason: "api_client_constructor_prefetch",
			});
		}
	}

	private getBaseUrl(): string {
		if (!this.baseUrl) {
			this.baseUrl = getApiBaseUrl({
				errorMessage:
					"[ApiClient] CRITICAL: BUN_PUBLIC_API_BASE_URL is missing in production build!",
			});
			console.log("[ApiClient] Base URL:", this.baseUrl);
		}
		return this.baseUrl;
	}

	private nextRequestId(): number {
		this.requestCounter += 1;
		return this.requestCounter;
	}

	private sanitizeHeadersForLog(
		headers: Record<string, string>,
	): Record<string, string> {
		const sanitized: Record<string, string> = {};
		for (const [key, value] of Object.entries(headers)) {
			const normalizedKey = key.toLowerCase();
			if (normalizedKey === "authorization") {
				sanitized[key] = value.startsWith("Bearer ")
					? ApiClient.REDACTED_AUTH_HEADER
					: ApiClient.REDACTED_SENSITIVE_HEADER;
				continue;
			}
			if (normalizedKey === MARCH_YAKNESS_SESSION_SIGNATURE_HEADER.toLowerCase()) {
				sanitized[key] = ApiClient.REDACTED_SENSITIVE_HEADER;
				continue;
			}
			sanitized[key] = value;
		}
		return sanitized;
	}

	private sanitizeHeadersForRequestLog(
		_endpoint: string,
		headers: Record<string, string>,
	): Record<string, string> {
		return this.sanitizeHeadersForLog(headers);
	}

	private isSensitivePayloadEndpoint(endpoint: string): boolean {
		let path = endpoint;
		try {
			path = new URL(endpoint, "https://api.local").pathname;
		} catch {
			path = endpoint.split("?")[0] ?? endpoint;
		}

		return [
			"/v1/earn/cash-outs",
			"/v1/earn/admin/cash-outs",
			"/v1/earn/reward-phones",
			"/v1/updates",
			"/api/earn-cash-outs",
		].some((prefix) => path.startsWith(prefix));
	}

	private sanitizePayloadForLog(endpoint: string, value: unknown): unknown {
		return this.isSensitivePayloadEndpoint(endpoint)
			? ApiClient.REDACTED_SENSITIVE_PAYLOAD
			: this.sanitizeValueForRequestLog(value);
	}

	private async attachMarchYaknessSessionSignature(
		headers: Record<string, string>,
		authToken: string,
		sessionUuid: string,
	): Promise<void> {
		headers[MARCH_YAKNESS_SESSION_SIGNATURE_HEADER] =
			await generateMarchYaknessSessionSignature({
				authToken,
				sessionUuid,
			});
	}

	private formatBackendReason(value: unknown): string | undefined {
		if (typeof value === "string") {
			const trimmed = value.trim();
			return trimmed || undefined;
		}

		if (typeof value === "number" || typeof value === "boolean") {
			return String(value);
		}

		if (Array.isArray(value)) {
			if (value.length === 0) return undefined;
			const first = this.formatBackendReason(value[0]);
			if (!first) return undefined;
			return value.length > 1 ? `${first} (+${value.length - 1} more)` : first;
		}

		if (!value || typeof value !== "object") {
			return undefined;
		}

		const candidate = value as Record<string, unknown>;
		if (typeof candidate.message === "string" && candidate.message.trim()) {
			return candidate.message.trim();
		}
		if (typeof candidate.error === "string" && candidate.error.trim()) {
			return candidate.error.trim();
		}

		try {
			const serialized = JSON.stringify(value);
			if (!serialized || serialized === "{}" || serialized === "[]") return undefined;
			return serialized.length > ApiClient.BACKEND_REASON_MAX_LENGTH
				? `${serialized.slice(0, ApiClient.BACKEND_REASON_MAX_LENGTH)}...`
				: serialized;
		} catch {
			return undefined;
		}
	}

	private extractBackendReason(errorData: Record<string, unknown>): string | undefined {
		const candidates: unknown[] = [
			errorData.reason,
			errorData.detail,
			errorData.details,
			errorData.validation,
			errorData.errors,
			errorData.hint,
		];

		for (const candidate of candidates) {
			const reason = this.formatBackendReason(candidate);
			if (reason) return reason;
		}

		return undefined;
	}

	private shouldLogVerboseResponseBody(): boolean {
		return (
			readPublicEnv("NODE_ENV") === "development" ||
			isLocalDevelopmentHost(globalThis.location?.hostname)
		);
	}

	private sanitizeValueForRequestLog(value: unknown): unknown {
		if (Array.isArray(value)) {
			return value.map((item) => this.sanitizeValueForRequestLog(item));
		}
		if (!value || typeof value !== "object") return value;
		if (typeof FormData !== "undefined" && value instanceof FormData) {
			return "<form data omitted>";
		}
		return Object.fromEntries(
			Object.entries(value).map(([key, nested]) => [
				key,
				/(?:authorization|cookie|credential|password|passcode|secret|signature|token|verification.?code|reset.?code|payment.?method|card.?number|cvc|cvv)/i.test(
					key,
				)
					? "[redacted]"
					: this.sanitizeValueForRequestLog(nested),
			]),
		);
	}

	private parseResponseBodyForLog(text: string, contentType: string | null): unknown {
		const trimmed = text.trim();
		if (!trimmed) {
			return "<empty>";
		}

		const looksJson =
			contentType?.includes("application/json") === true ||
			trimmed.startsWith("{") ||
			trimmed.startsWith("[");

		if (!looksJson) {
			return trimmed;
		}

		try {
			return JSON.parse(trimmed);
		} catch {
			return trimmed;
		}
	}

	private formatStructuredLogValue(value: unknown): string {
		if (value == null) {
			return "<empty>";
		}

		if (typeof value === "string") {
			return value;
		}

		try {
			return JSON.stringify(value, null, 2);
		} catch {
			return String(value);
		}
	}

	/**
	 * Get the auth token from window or localStorage
	 */
	private getAuthToken(): string | null {
		// First check window.APP_AUTH_TOKEN (injected by webview)
		if (window.APP_AUTH_TOKEN) {
			return window.APP_AUTH_TOKEN;
		}

		// Check environment variable (injected by bun dev/build)
		const envAuthToken = getPublicAuthToken();
		if (envAuthToken) {
			return envAuthToken;
		}

		const injectedToken = localStorage.getItem(STORAGE_KEYS.APP_AUTH_TOKEN);
		if (injectedToken) return injectedToken;

		// Fallback to localStorage
		const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
		return token;
	}

	/**
	 * Wait for the auth token to become available.
	 * iOS injects window.APP_AUTH_TOKEN after page load via didFinish,
	 * so early API calls may fire before it's set. This waits for the
	 * authTokenReady event (dispatched by iOS) or polls for the token.
	 */
	private waitForAuthToken(
		timeoutMs = ApiClient.AUTH_TOKEN_WAIT_TIMEOUT_MS,
	): Promise<string> {
		const token = this.getAuthToken();
		if (token) {
			console.log("[ApiClient] waitForAuthToken resolved immediately from existing token.");
			return Promise.resolve(token);
		}
		void hydrateAppContextFromNativeBridge({
			reason: "wait_for_auth_token_preflight",
		});
		console.log("[ApiClient] waitForAuthToken started.", { timeoutMs });

		if (this.tokenReadyPromise) return this.tokenReadyPromise;

			this.tokenReadyPromise = new Promise<string>((resolve, reject) => {
				const cleanup = () => {
					clearTimeout(timeout);
					clearInterval(interval);
					window.removeEventListener("authTokenReady", handler);
					this.tokenReadyPromise = null;
				};

				const timeout = setTimeout(() => {
					cleanup();
					console.warn("[ApiClient] waitForAuthToken timed out with no token.", { timeoutMs });
					reject(new Error("No auth token available"));
				}, timeoutMs);

				// Listen for the authTokenReady event dispatched by iOS after injection
				const handler = () => {
					const t = this.getAuthToken();
					if (t) {
						cleanup();
						console.log("[ApiClient] waitForAuthToken resolved via authTokenReady event.");
						resolve(t);
					}
				};
				window.addEventListener("authTokenReady", handler);

				// Also poll in case the event already fired or token appears via another path
				const interval = setInterval(() => {
					const t = this.getAuthToken();
					if (t) {
						cleanup();
						console.log("[ApiClient] waitForAuthToken resolved via polling.");
						resolve(t);
					}
				}, 50);
			});

		return this.tokenReadyPromise;
	}

	async getCurrentAuthToken(): Promise<string | null> {
		let token = this.getAuthToken();
		if (!token) {
			await hydrateAppContextFromNativeBridge({
				reason: "get_current_auth_token",
			});
			token = this.getAuthToken();
		}

		if (token) {
			return token;
		}

		try {
			return await this.waitForAuthToken();
		} catch {
			return this.getAuthToken();
		}
	}

	/**
	 * Make an authenticated request
	 */
	async request<T = any>(
		endpoint: string,
		config: RequestConfig = {},
	): Promise<T> {
		const {
			method = "GET",
			body,
			headers = {},
			requiresAuth = true,
			marchYaknessSessionUuid,
			signal,
			silent = false,
		} = config;
		const requestId = this.nextRequestId();
		const requestPrefix = `[ApiClient#${requestId}]`;

		const isFormDataBody =
			typeof FormData !== "undefined" && body instanceof FormData;

		// Build headers. Bodyless requests should not advertise a JSON content type,
		// because some endpoints validate media-related query requests strictly.
		// FormData must also omit Content-Type so the browser can set the boundary.
		const requestHeaders: Record<string, string> = {
			...headers,
		};
		const hasExplicitContentType = Object.keys(requestHeaders).some(
			(key) => key.toLowerCase() === "content-type",
		);
		if (body !== undefined && !hasExplicitContentType && !isFormDataBody) {
			requestHeaders["Content-Type"] = "application/json";
		}

		// Add auth token if required
		if (requiresAuth) {
			let token = this.getAuthToken();
			if (!token) {
				console.log(`${requestPrefix} No token before request; attempting getAppContext hydration.`, {
					method,
					endpoint,
				});
				await hydrateAppContextFromNativeBridge({
					reason: `request_pre_auth:${method}:${endpoint}`,
				});
				token = this.getAuthToken();
			}

			if (token) {
				console.log(`${requestPrefix} Auth token available for request.`, {
					method,
					endpoint,
				});
				requestHeaders["Authorization"] = `Bearer ${token}`;
			// If token isn't available yet, wait for iOS to inject it
			} else {
				try {
					token = await this.waitForAuthToken();
					requestHeaders["Authorization"] = `Bearer ${token}`;
				} catch {
					// One last synchronous check guards against a timeout-boundary race:
					// token may have appeared just after waitForAuthToken rejected.
					token = this.getAuthToken();
					if (token) {
						requestHeaders["Authorization"] = `Bearer ${token}`;
					} else {
						// In development/local mode, suppress the error to allow UI testing without auth
						if (
							readPublicEnv("NODE_ENV") === "development" ||
							isLocalDevelopmentHost(globalThis.location?.hostname)
						) {
							console.warn(
								"[ApiClient] No auth token found. Suppressing error for local development.",
							);
							return Promise.resolve({} as T);
						}
						throw new Error("No auth token available");
					}
				}
			}
		}

		if (marchYaknessSessionUuid) {
			const authorizationHeader = requestHeaders["Authorization"];
			const authToken = authorizationHeader?.startsWith("Bearer ")
				? authorizationHeader.slice("Bearer ".length).trim()
				: "";
			if (!authToken) {
				throw new Error("March Yakness session signature requires an authenticated request.");
			}
			await this.attachMarchYaknessSessionSignature(
				requestHeaders,
				authToken,
				marchYaknessSessionUuid,
			);
		}

		// Build URL
		const url = endpoint.startsWith("http")
			? endpoint
			: `${this.getBaseUrl()}${endpoint.startsWith("/") ? endpoint : "/" + endpoint
			}`;

		console.log(`${requestPrefix} Request: ${method} ${url}`);
		console.log(
			`${requestPrefix} Headers:`,
			this.sanitizeHeadersForRequestLog(endpoint, requestHeaders),
		);
		const requestBodyForLog = this.sanitizePayloadForLog(endpoint, body);
		if (this.shouldLogVerboseResponseBody()) {
			console.log(`${requestPrefix} Body:\n${this.formatStructuredLogValue(requestBodyForLog)}`);
		} else {
			console.log(`${requestPrefix} Body:`, requestBodyForLog ?? "<empty>");
		}

		// Make request
		try {
			const requestStartAt = Date.now();
			const response = await fetch(url, {
				method,
				headers: requestHeaders,
				signal,
				body:
					body === undefined
						? undefined
						: isFormDataBody
							? body
							: JSON.stringify(body),
			});

			const durationMs = Date.now() - requestStartAt;
			const contentType = response.headers.get("content-type");
			const responseText = response.status === 204 ? "" : await response.text();
			const parsedResponseBody = this.parseResponseBodyForLog(responseText, contentType);
			const responseBodyForLog = this.sanitizePayloadForLog(
				endpoint,
				parsedResponseBody,
			);
				console.log(`${requestPrefix} Response:`, {
					method,
					url,
					status: response.status,
					ok: response.ok,
					durationMs,
					contentType,
				});
				if (this.shouldLogVerboseResponseBody()) {
					console.log(
						`${requestPrefix} Response Body:\n${this.formatStructuredLogValue(responseBodyForLog)}`,
					);
				}

			// Handle non-ok responses
			if (!response.ok) {
				const errorData =
					parsedResponseBody &&
					typeof parsedResponseBody === "object" &&
					!Array.isArray(parsedResponseBody)
						? (parsedResponseBody as Record<string, unknown>)
						: {};
				// Some validators return an issue array as the whole response. Preserve
				// it on ApiError so a product surface can choose a concise field message
				// instead of rendering a serialized implementation payload to a user.
				const errorPayload = Array.isArray(parsedResponseBody)
					? { validation: parsedResponseBody }
					: errorData;
				const errorMessage =
					(typeof errorData.message === "string" && errorData.message) ||
					(typeof errorData.error === "string" && errorData.error) ||
					`Request failed with status ${response.status}`;
				const errorCode =
					(typeof errorData.code === "string" && errorData.code) ||
					(typeof errorData.error_code === "string" && errorData.error_code) ||
					undefined;
				const backendReason = this.extractBackendReason(errorData);
				const apiError = new ApiError(
					errorMessage,
					response.status,
					errorCode,
					backendReason,
					errorPayload,
				);
				if (!silent) {
					const reasonSuffix = backendReason ? ` reason="${backendReason}"` : "";
					console.error(
						`${requestPrefix} Request failed: ${method} ${url} status=${response.status}${reasonSuffix}`,
						responseBodyForLog,
					);
					captureError(apiError, {
						method,
						url,
						status: response.status,
						requestBody: requestBodyForLog,
						responseBody: responseBodyForLog,
						errorMessage,
						backendReason,
					});
				}
				throw apiError;
			}

			// 204 No Content — nothing to parse
			if (response.status === 204) {
				return {} as T;
			}

			// Parse response
			if (!responseText) {
				return {} as T;
			}
			try {
				return JSON.parse(responseText) as T;
			} catch (parseError) {
				console.error(`${requestPrefix} JSON parse failed:`, {
					method,
					url,
					status: response.status,
					contentType,
					bodyPreview: this.isSensitivePayloadEndpoint(endpoint)
						? ApiClient.REDACTED_SENSITIVE_PAYLOAD
						: responseText.slice(0, 500),
				});
				throw parseError;
			}
		} catch (error) {
			// Re-throw ApiError as-is
			if (error instanceof ApiError) {
				throw error;
			}

			// Wrap other errors
			const message =
				error instanceof Error ? error.message : "Unknown error occurred";
			if (!silent) {
				console.error(`${requestPrefix} Unexpected request error:`, {
					method,
					url,
					error: message,
				});
				const isNetworkError = isNetworkRequestError(error);
				if (isNetworkError) {
					captureMessage('Network request failed', 'warning', {
						method,
						url,
						errorMessage: message,
					});
				} else {
					captureError(error, {
						method,
						url,
						requestBody: requestBodyForLog,
						errorMessage: message,
					});
				}
			}
			throw new ApiError(
				message,
				0,
			);
		}
	}

	/**
	 * Convenience methods
	 */
	async get<T = any>(endpoint: string, opts?: Pick<RequestConfig, "requiresAuth" | "silent" | "headers" | "marchYaknessSessionUuid" | "signal">): Promise<T> {
		const { requiresAuth = true, silent = false, headers, marchYaknessSessionUuid, signal } = opts ?? {};
		return this.request<T>(endpoint, { method: "GET", requiresAuth, silent, headers, marchYaknessSessionUuid, signal });
	}

	async post<T = any>(endpoint: string, body?: any, opts?: Pick<RequestConfig, "requiresAuth" | "silent" | "headers" | "marchYaknessSessionUuid" | "signal">): Promise<T> {
		const { requiresAuth = true, silent = false, headers, marchYaknessSessionUuid, signal } = opts ?? {};
		return this.request<T>(endpoint, { method: "POST", body, requiresAuth, silent, headers, marchYaknessSessionUuid, signal });
	}

	async put<T = any>(endpoint: string, body?: any, opts?: Pick<RequestConfig, "requiresAuth" | "silent" | "headers" | "marchYaknessSessionUuid" | "signal">): Promise<T> {
		const { requiresAuth = true, silent = false, headers, marchYaknessSessionUuid, signal } = opts ?? {};
		return this.request<T>(endpoint, { method: "PUT", body, requiresAuth, silent, headers, marchYaknessSessionUuid, signal });
	}

	async delete<T = any>(endpoint: string, opts?: Pick<RequestConfig, "requiresAuth" | "silent" | "headers" | "marchYaknessSessionUuid" | "signal">): Promise<T> {
		const { requiresAuth = true, silent = false, headers, marchYaknessSessionUuid, signal } = opts ?? {};
		return this.request<T>(endpoint, { method: "DELETE", requiresAuth, silent, headers, marchYaknessSessionUuid, signal });
	}

	async patch<T = any>(endpoint: string, body?: any, opts?: Pick<RequestConfig, "requiresAuth" | "silent" | "headers" | "marchYaknessSessionUuid" | "signal">): Promise<T> {
		const { requiresAuth = true, silent = false, headers, marchYaknessSessionUuid, signal } = opts ?? {};
		return this.request<T>(endpoint, { method: "PATCH", body, requiresAuth, silent, headers, marchYaknessSessionUuid, signal });
	}
}

// Export singleton instance
export const apiClient = new ApiClient();
export const getCurrentAuthToken = () => apiClient.getCurrentAuthToken();

// Export class for custom instances
export default ApiClient;

type ImportMetaWithEnv = ImportMeta & {
	readonly env?: Record<string, string | undefined>;
};

type GlobalWithProcessEnv = typeof globalThis & {
	readonly process?: {
		readonly env?: Record<string, string | undefined>;
	};
};

const readPublicEnv = (key: string): string | undefined => {
	const importMetaEnv = (import.meta as ImportMetaWithEnv).env;
	if (importMetaEnv?.[key]) {
		return importMetaEnv[key];
	}

	return (globalThis as GlobalWithProcessEnv).process?.env?.[key];
};
